import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '../contexts/UnifiedAuthContext';
import { usePaymentStatus } from '../contexts/PaymentStatusContext';
import useDestinationVariant from './useDestinationVariant';
import {
  getAgentHandoff,
  getDestinationVariant,
  setAgentHandoff,
} from '../utils/aiAgentHandoff';
import { savePromptToSession } from '../utils/aiPromptStorage';

const SAMPLE_PROMPTS = {
  lumibot_readme_deploy:
    'Create a sample Lumibot strategy, run a supported backtest on BotSpot, compare the results, and show me how I would start a paper deployment with monitoring.',
  lumibot_deploy_sample:
    'Create a sample Lumibot strategy, run a supported backtest on BotSpot, compare the results, and show me how I would start a paper deployment with monitoring.',
};

const FORWARDED_QUERY_KEYS = [
  'dv',
  'source',
  'sample',
  'prompt_template',
  'handoff',
  'mv',
  '$feature/sales_page_signup_framing_v4_variant',
  '$feature_flag_sales_page_signup_framing_v4_variant',
  'feature_flag',
  'sales_page_signup_framing_v4_experiment_name',
  'sales_page_signup_framing_variant',
  'sales_page_signup_framing_v4_variant',
  'sales_page_signup_framing_v4_route_variant',
  'sales_page_form_variant',
  'sales_page_framing_variant',
  'sales_page_design_variant',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

export function getSalesPromptIntent(searchParams) {
  const rawPrompt = searchParams.get('prompt') || '';
  const prompt = rawPrompt.trim();
  const sample = (searchParams.get('sample') || searchParams.get('prompt_template') || '').trim();
  const handoff = (searchParams.get('handoff') || '').trim();
  const samplePrompt = SAMPLE_PROMPTS[sample] || '';

  return {
    hasIntent: Boolean(prompt || samplePrompt || handoff),
    prompt: prompt || samplePrompt,
    sample,
    handoff,
    promptTransport: handoff ? 'handoff_token' : sample ? 'sample_template' : prompt ? 'query' : null,
    promptSource: searchParams.get('utm_source') || searchParams.get('source') || 'sales',
  };
}

function mergeSearchParams(targetParams, sourceParams) {
  FORWARDED_QUERY_KEYS.forEach((key) => {
    const value = sourceParams.get(key);
    if (value && !targetParams.has(key)) {
      targetParams.set(key, value);
    }
  });
}

function getPromptPageUrl(baseParams, sourceParams) {
  const params = new URLSearchParams(baseParams);
  mergeSearchParams(params, sourceParams);
  const queryString = params.toString();
  return queryString ? `/sales-ai-prompt?${queryString}` : '/sales-ai-prompt';
}

function getSalesChooseUrl(baseParams, sourceParams) {
  const params = new URLSearchParams(baseParams);
  mergeSearchParams(params, sourceParams);
  const queryString = params.toString();
  return queryString ? `/sales-choose?${queryString}` : '/sales-choose';
}

export default function useSalesPromptRouting({ pageVariant = 'control' } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, handleCustomJwtAuth } = useUnifiedAuth();
  const {
    isPayingCustomer,
    isLoading: paymentLoading,
  } = usePaymentStatus();
  const {
    destinationVariant,
    isLoading: destinationLoading,
    updateHandoffWithDestination,
    getDestinationPath,
  } = useDestinationVariant();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const showLoginHint = searchParams.get('showLogin') === '1';
  const loginReturnTo = searchParams.get('successUrl') || getSalesChooseUrl(new URLSearchParams(), searchParams);
  const promptIntent = useMemo(() => getSalesPromptIntent(searchParams), [searchParams]);

  const seedPromptIntent = useCallback(() => {
    if (!promptIntent.hasIntent) return null;

    if (promptIntent.prompt) {
      savePromptToSession(promptIntent.prompt);
    }

    const handoff = getAgentHandoff() || {};
    const updatedHandoff = {
      ...handoff,
      source: promptIntent.promptSource,
      promptSource: promptIntent.promptSource,
      promptTransport: promptIntent.promptTransport,
      salesPageVariant: pageVariant,
    };

    if (promptIntent.prompt) {
      updatedHandoff.prompt = promptIntent.prompt;
    }
    if (promptIntent.sample) {
      updatedHandoff.sample = promptIntent.sample;
    }
    if (promptIntent.handoff) {
      updatedHandoff.handoffToken = promptIntent.handoff;
    }

    setAgentHandoff(updatedHandoff);
    return updatedHandoff;
  }, [pageVariant, promptIntent]);

  useEffect(() => {
    seedPromptIntent();
  }, [seedPromptIntent]);

  const initialAuthCapturedRef = useRef(false);
  const wasAuthedOnLandingRef = useRef(false);

  useEffect(() => {
    const waitingForPayment = isAuthenticated && paymentLoading;
    if (authLoading || destinationLoading || waitingForPayment) return;

    if (!initialAuthCapturedRef.current) {
      initialAuthCapturedRef.current = true;
      wasAuthedOnLandingRef.current = isAuthenticated;
    }

    if (!wasAuthedOnLandingRef.current || !isAuthenticated) return;

    if (isPayingCustomer) {
      seedPromptIntent();
      navigate('/ai-agent', { replace: true });
      return;
    }

    if (promptIntent.hasIntent) {
      seedPromptIntent();
      updateHandoffWithDestination();
      const params = new URLSearchParams();
      params.set('dv', destinationVariant);
      navigate(getPromptPageUrl(params, searchParams), { replace: true });
      return;
    }

    const persistedVariant = getDestinationVariant();
    if (persistedVariant) {
      const destinationPath = persistedVariant === 'checkout'
        ? '/checkout/starter-plan?redirect=starter_plan'
        : '/ai-agent';
      navigate(destinationPath, { replace: true });
      return;
    }

    updateHandoffWithDestination();
    navigate(getDestinationPath(), { replace: true });
  }, [
    authLoading,
    destinationLoading,
    destinationVariant,
    getDestinationPath,
    isAuthenticated,
    isPayingCustomer,
    navigate,
    paymentLoading,
    promptIntent.hasIntent,
    searchParams,
    seedPromptIntent,
    updateHandoffWithDestination,
  ]);

  const getLeadSuccessPath = useCallback((baseParams, autoLoginToken) => {
    const params = new URLSearchParams(baseParams);
    if (autoLoginToken && typeof handleCustomJwtAuth === 'function') {
      Promise.resolve(handleCustomJwtAuth(autoLoginToken)).catch((error) => {
        console.warn('[useSalesPromptRouting] Lead auto-login failed', error);
      });
    }

    if (promptIntent.hasIntent) {
      seedPromptIntent();
      return getPromptPageUrl(params, searchParams);
    }

    return getSalesChooseUrl(params, searchParams);
  }, [handleCustomJwtAuth, promptIntent.hasIntent, searchParams, seedPromptIntent]);

  return {
    showLoginHint,
    loginReturnTo,
    hasPromptIntent: promptIntent.hasIntent,
    getLeadSuccessPath,
    seedPromptIntent,
  };
}
